/* eslint-disable no-underscore-dangle */
import { SearchableAnalysisInDecision, SearchableDecisionInstantSearch } from '@livv/models';
import { Hit, HitAttributeSnippetResult } from 'instantsearch.js';
import { useMemo } from 'react';
import { getSnippetedAttributes } from '@utils/search/snippet';

interface SnippetAnalysis {
    analyses?: {
        fullText?: HitAttributeSnippetResult;
    }[];
}

interface HighlightAnalysis {
    analyses?: SearchableAnalysisInDecision[];
}

export interface HitWithSnippetResult {
    analyses?: SearchableAnalysisInDecision[];
    _snippetResult?: SnippetAnalysis;
    _highlightResult?: HighlightAnalysis;
}

interface HookResult {
    hitWithFirstTwoAnalyses?: SearchableDecisionInstantSearch;
    hitAnalysisForDropdown?: SearchableDecisionInstantSearch;
    snippetAnalysesHasMatch: boolean;
}

const useSnippetAnalysis = (
    hit: Hit<SearchableDecisionInstantSearch> & HitWithSnippetResult,
): HookResult => {
    const { hitAnalysisForDropdown, hitWithFirstTwoAnalyses, snippetAnalysesHasMatch } =
        useMemo(() => {
            if (!hit) {
                return {
                    hitAnalysisForDropdown: undefined,
                    hitWithFirstTwoAnalyses: undefined,
                    snippetAnalysesHasMatch: false,
                };
            }

            const hasMatch = getSnippetedAttributes(hit).includes('analyses');

            if (hasMatch) {
                const firstTwoAnalysesGroup: HitWithSnippetResult = {
                    _snippetResult: { analyses: [] },
                    analyses: [],
                };
                const remainingAnalysesGroup: HitWithSnippetResult = {
                    _snippetResult: { analyses: [] },
                    analyses: [],
                };

                let matchCount = 0;

                hit._snippetResult?.analyses?.forEach(({ fullText }, index) => {
                    if (fullText?.matchLevel !== 'none' && matchCount < 2) {
                        firstTwoAnalysesGroup?.analyses?.push(hit.analyses![index]);
                        if (hit._snippetResult?.analyses) {
                            firstTwoAnalysesGroup._snippetResult?.analyses?.push(
                                hit._snippetResult.analyses[index],
                            );
                            matchCount++;
                        }
                    }
                    if (!firstTwoAnalysesGroup?.analyses?.includes(hit.analyses![index])) {
                        remainingAnalysesGroup.analyses?.push(hit.analyses![index]);
                        if (hit._snippetResult?.analyses) {
                            remainingAnalysesGroup._snippetResult?.analyses?.push(
                                hit._snippetResult.analyses[index],
                            );
                        }
                    }
                });

                return {
                    hitAnalysisForDropdown: {
                        ...hit,
                        _snippetResult: {
                            ...(hit._snippetResult as HitWithSnippetResult['_snippetResult']),
                            analyses: remainingAnalysesGroup?._snippetResult?.analyses,
                        },
                        analyses: remainingAnalysesGroup.analyses || [],
                    },
                    hitWithFirstTwoAnalyses: {
                        ...hit,
                        _snippetResult: {
                            ...(hit._snippetResult as HitWithSnippetResult['_snippetResult']),
                            analyses: firstTwoAnalysesGroup?._snippetResult?.analyses,
                        },
                        analyses: firstTwoAnalysesGroup.analyses || [],
                    },
                    snippetAnalysesHasMatch: hasMatch,
                };
            }

            return {
                hitAnalysisForDropdown: { ...hit, analyses: hit.analyses?.slice(2) || [] },
                hitWithFirstTwoAnalyses: { ...hit, analyses: hit.analyses?.slice(0, 2) || [] },
                snippetAnalysesHasMatch: hasMatch,
            };
        }, [hit]);

    return {
        hitAnalysisForDropdown,
        hitWithFirstTwoAnalyses,
        snippetAnalysesHasMatch,
    };
};

export default useSnippetAnalysis;
